/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {APP_INITIALIZER, ErrorHandler, NgModule, NgZone} from '@angular/core';
import {BrowserModule, Title} from '@angular/platform-browser';
import {AppComponent} from './app.component';
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {SharedAppReducer} from '@common-core/store/reducers';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {environment} from '@paas-environments/environment';
import {AppRouting} from './app.routing';
import {PaasInterceptorsModule} from '@paas-core/interceptors/paas-interceptors.module';
import {StoreModule, MetaReducer} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {PspNotificationModule} from '@common-core/notification/psp-notification.module';
import {PspTranslateModule} from '@common-config/translate/psp-translate.module';
import {PspLocaleModule} from '@common-config/locale/psp-locale.module';
import {AuthEffects} from '@shared/modules/auth/store/effects/auth.effects';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {DateFnsModule} from 'ngx-date-fns';
import {NgxPermissionsModule} from 'ngx-permissions';
import {firstValueFrom, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {setDisposableDomains} from '@common-core/helpers/validation/custom-validator/disposable-email-cache';
import {hideJsdWidgetHelpButton} from '@common-core/helpers/jsd-widget/jsd-widget.helper';
import {TokenEffects} from '@common-core/store/effects/token.effects';
import {AppEffects} from '@common-core/store/effects/app.effects';
import {APP_CONFIG} from '@common-config/app-config';
import {EnvironmentConfig} from '@shared/models/environment-config';
import {TranslateService} from '@ngx-translate/core';
import {ThemeEffects} from '@common-core/store/effects/theme.effects';
import {AppMetaReducer} from '@common-core/store/reducers';
import {
  SocialLoginModule,
  SocialAuthServiceConfig,
  GoogleLoginProvider,
} from '@abacritt/angularx-social-login';
import * as Sentry from '@sentry/angular';
import {Router} from '@angular/router';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';
import {ObfuscateService} from '@common-services/encrypt/obfuscate.service';
import {UserActivityService} from '@common-services/user-activity/user-activity.service';
import {PresignedUrlService} from '@common-services/presigned-url/presigned-url.service';
import {ProjectService} from '@common-services/project/project.service';
import {ENVIRONMENT_TOKEN} from '@shared/common-core/environments/environment.token';

export const metaReducers: MetaReducer[] = [AppMetaReducer.resetStoreMetaReducer];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRouting,
    BrowserAnimationsModule,
    DateFnsModule.forRoot(),
    PspLocaleModule.forRoot(),
    PspTranslateModule.forRoot(),
    PaasInterceptorsModule.forRoot(),
    PspNotificationModule.forRoot(),
    StoreModule.forRoot(SharedAppReducer.sharedAppReducer, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
      metaReducers,
    }),
    EffectsModule.forRoot([AuthEffects, TokenEffects, AppEffects, ThemeEffects]),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 25,
          logOnly: environment.production,
        })
      : [],
    StoreRouterConnectingModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    SocialLoginModule,
  ],
  providers: [
    LocalStorageService,
    ObfuscateService,
    UserActivityService,
    PresignedUrlService,
    ProjectService,
    {
      provide: APP_INITIALIZER,
      useFactory: (ngZone: NgZone) => () => hideJsdWidgetHelpButton(ngZone),
      deps: [NgZone],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          appConfig: EnvironmentConfig,
          translateService: TranslateService,
          titleService: Title
        ) =>
        async () => {
          // translateService.get() (unlike instant()) waits for the translation file to finish
          // loading over HTTP before resolving, so this must not be replaced with instant() -
          // instant() returns the raw, untranslated key if the translations haven't loaded yet.
          const environmentName = await firstValueFrom(
            translateService
              .get(`header.environment.${appConfig.cloudEnvironment}`)
              .pipe(catchError(() => of(appConfig.cloudEnvironment)))
          );
          titleService.setTitle(`${environmentName} - Passport`);
        },
      deps: [APP_CONFIG, TranslateService, Title],
      multi: true,
    },
    {
      provide: 'SocialAuthServiceConfig',
      useFactory: (appConfig: EnvironmentConfig) => {
        return {
          autoLogin: false,
          providers: [
            {
              id: GoogleLoginProvider.PROVIDER_ID,
              provider: new GoogleLoginProvider(appConfig.clientId), // clientID retrieved from app-config.json file
            },
          ],
          onError: err => {
            console.error(err);
          },
        } as SocialAuthServiceConfig;
      },
      deps: [APP_CONFIG],
    },
    // SENTRY - START
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    // SENTRY - END
    {
      provide: APP_INITIALIZER,
      useFactory: (http: HttpClient) => () =>
        firstValueFrom(
          http.get<string[]>('./assets/data/disposable-email-addresses.json').pipe(
            tap(setDisposableDomains),
            catchError(() => of([]))
          )
        ),
      deps: [HttpClient],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    {provide: ENVIRONMENT_TOKEN, useValue: environment},
  ],
})
export class AppModule {}
